/**
 * Collection sidebar facets helpers:
 * - Keep price-range bound labels in sync with the thumbs.
 * - Persist facet-form details open/closed state in sessionStorage so
 *   filter clicks (URL change / Liquid re-render) do not snap accordions.
 */
(function () {
  'use strict';

  const STORAGE_PREFIX = 'collection-facet-details:';
  let isRestoring = false;

  /**
   * @returns {string}
   */
  function storageKey() {
    return STORAGE_PREFIX + window.location.pathname;
  }

  /**
   * @param {Element} details
   * @returns {string}
   */
  function detailsKey(details) {
    return details.getAttribute('data-facet-accordion') || details.id || '';
  }

  /**
   * @returns {Record<string, boolean> | null}
   */
  function readState() {
    try {
      const raw = window.sessionStorage.getItem(storageKey());
      if (raw == null) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed;
      }
      if (Array.isArray(parsed)) {
        const map = {};
        parsed.forEach((id) => {
          map[id] = true;
        });
        return map;
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * @param {Record<string, boolean>} state
   */
  function writeState(state) {
    try {
      window.sessionStorage.setItem(storageKey(), JSON.stringify(state));
    } catch (error) {
      // Ignore quota / private-mode failures.
    }
  }

  /**
   * Set open state without AccordionDisclosure's height animation.
   * @param {HTMLDetailsElement} details
   * @param {boolean} shouldOpen
   */
  function setDetailsOpen(details, shouldOpen) {
    details._open = shouldOpen;
    if (shouldOpen) {
      details.setAttribute('open', '');
      details.setAttribute('aria-expanded', 'true');
    } else {
      details.removeAttribute('open');
      details.setAttribute('aria-expanded', 'false');
    }
    details.style.height = 'auto';
    details.style.overflow = 'visible';
  }

  /**
   * @param {HTMLElement} form
   * @param {Record<string, boolean>} state
   */
  function applyState(form, state) {
    form.querySelectorAll('details[data-facet-accordion], details[id]').forEach((details) => {
      const key = detailsKey(details);
      const id = details.id;
      let shouldOpen;
      if (key && Object.prototype.hasOwnProperty.call(state, key)) {
        shouldOpen = !!state[key];
      } else if (id && Object.prototype.hasOwnProperty.call(state, id)) {
        shouldOpen = !!state[id];
      } else {
        return;
      }
      setDetailsOpen(details, shouldOpen);
    });
  }

  /**
   * @param {ParentNode} [scope]
   */
  function restoreAll(scope) {
    const state = readState();
    if (!state) return;

    isRestoring = true;
    try {
      const root = scope || document;
      root.querySelectorAll('form[is="facet-form"]').forEach((form) => {
        applyState(form, state);
      });
    } finally {
      isRestoring = false;
    }
  }

  /**
   * Merge this form's current open/closed flags into sessionStorage.
   * Keys for groups missing from this form are kept, so a group whose
   * options change (e.g. product_model) still remembers a closed state.
   * @param {HTMLElement} form
   */
  function persistForm(form) {
    if (isRestoring) return;

    const next = Object.assign({}, readState() || {});
    form.querySelectorAll('details[data-facet-accordion], details[id]').forEach((details) => {
      const key = detailsKey(details);
      if (!key) return;
      const isOpen = !!details.open;
      next[key] = isOpen;
      if (details.id && details.id !== key) {
        next[details.id] = isOpen;
      }
    });
    writeState(next);
  }

  /**
   * Shop money format used by product price-list (`money` vs `money_with_currency`).
   * @returns {string}
   */
  function shopMoneyFormat() {
    const settings = (window.themeVariables && window.themeVariables.settings) || {};
    if (settings.currencyCodeEnabled) {
      return settings.moneyWithCurrencyFormat || settings.moneyFormat || '{{amount}}';
    }
    return settings.moneyFormat || '{{amount}}';
  }

  /**
   * Format whole-currency units (price filter values) with the shop money format.
   * @param {string|number} majorUnits
   * @returns {string}
   */
  function formatFacetPrice(majorUnits) {
    const cents = Math.round(Number(majorUnits) * 100);
    if (!Number.isFinite(cents)) return String(majorUnits);
    const formatString = shopMoneyFormat();
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = formatString.match(placeholderRegex);
    if (!match) return String(majorUnits);

    function defaultTo(value, fallback) {
      return value == null || value !== value ? fallback : value;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ',');
      decimal = defaultTo(decimal, '.');
      if (isNaN(number) || number == null) return '0';
      const parts = (number / 100).toFixed(precision).split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }

    let value = '';
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_space_separator':
        // Shopify: 1 134,65 (the decimal mark is a comma).
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_no_decimals_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 0, "'");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  /**
   * Prefer the number fields while typing; fall back to the range thumbs
   * when a field is empty (reset / no active price filter).
   * @param {HTMLInputElement|null} textInput
   * @param {HTMLInputElement|null} rangeInput
   * @returns {string|null}
   */
  function boundMajorUnits(textInput, rangeInput) {
    if (textInput && textInput.value !== '' && Number.isFinite(Number(textInput.value))) {
      return textInput.value;
    }
    return rangeInput ? rangeInput.value : null;
  }

  /**
   * @param {HTMLElement} rangeRoot
   */
  function syncBounds(rangeRoot) {
    const bounds = rangeRoot.querySelector('.price-range__bounds');
    if (!bounds) return;

    const minEl = bounds.querySelector('[data-bound="min"]');
    const maxEl = bounds.querySelector('[data-bound="max"]');
    const minText = rangeRoot.querySelector('input[name="filter.v.price.gte"]');
    const maxText = rangeRoot.querySelector('input[name="filter.v.price.lte"]');
    const minRange = rangeRoot.querySelector('.range-group input[type="range"]:first-child');
    const maxRange = rangeRoot.querySelector('.range-group input[type="range"]:last-child');
    const minVal = boundMajorUnits(minText, minRange);
    const maxVal = boundMajorUnits(maxText, maxRange);

    if (minEl && minVal != null) minEl.textContent = formatFacetPrice(minVal);
    if (maxEl && maxVal != null) maxEl.textContent = formatFacetPrice(maxVal);
  }

  /**
   * @param {Event} event
   */
  function onRangeEvent(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const rangeRoot = target.closest('price-range');
    if (!rangeRoot) return;

    if (!target.matches('input[type="range"], input[type="number"]')) return;

    syncBounds(rangeRoot);
  }

  /**
   * Persist as soon as AccordionDisclosure flips `_open`, without waiting
   * for the close animation to remove the native `open` attribute.
   * @param {Event} event
   */
  function onSummaryClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const summary = target.closest('form[is="facet-form"] details > summary');
    if (!summary) return;

    const form = summary.closest('form[is="facet-form"]');
    if (!form) return;

    queueMicrotask(() => persistForm(form));
  }

  /**
   * Snapshot accordion state before the section request is sent.
   * @param {Event} event
   */
  function onFormChange(event) {
    onRangeEvent(event);
    const target = event.target;
    if (!(target instanceof Element)) return;

    const form = target.closest('form[is="facet-form"]');
    if (form) persistForm(form);
  }

  /**
   * @param {Event} event
   */
  function onDetailsToggle(event) {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;

    const form = details.closest('form[is="facet-form"]');
    if (!form) return;

    persistForm(form);
  }

  /**
   * Re-apply cached accordion state after Shopify section morph.
   * @param {CustomEvent} event
   */
  function onFacetUpdate(event) {
    const url = event.detail && event.detail.url;
    const sectionId = url && url.searchParams ? url.searchParams.get('section_id') : null;
    const section = sectionId
      ? document.getElementById(`shopify-section-${sectionId}`)
      : document.querySelector('.collection')?.closest('.shopify-section');
    if (!section) return;

    const observer = new MutationObserver(() => {
      if (section.classList.contains('is-loading')) return;
      observer.disconnect();
      restoreAll(section);
    });
    observer.observe(section, { childList: true, attributes: true, attributeFilter: ['class'] });
  }

  document.addEventListener('click', onSummaryClick, true);
  document.addEventListener('input', onRangeEvent);
  document.addEventListener('change', onFormChange, true);
  document.addEventListener('toggle', onDetailsToggle, true);
  document.addEventListener('facet:update', onFacetUpdate);
  document.addEventListener('shopify:section:load', (event) => restoreAll(event.target));

  restoreAll();

  /**
   * Collection filter sheet is a bottom drawer through tablet (Figma 69:11116).
   * Theme Drawer.openFrom only uses bottom below 700px.
   */
  function patchCollectionDrawerOpenFrom() {
    const FacetDrawer = window.customElements.get('facet-drawer');
    if (!FacetDrawer || FacetDrawer.prototype._collectionPadOpenFrom) {
      return;
    }
    const parentProto = Object.getPrototypeOf(FacetDrawer.prototype);
    const original =
      Object.getOwnPropertyDescriptor(parentProto, 'openFrom') ||
      Object.getOwnPropertyDescriptor(FacetDrawer.prototype, 'openFrom');
    if (!original || typeof original.get !== 'function') {
      return;
    }
    Object.defineProperty(FacetDrawer.prototype, 'openFrom', {
      configurable: true,
      get() {
        if (
          this.classList.contains('collection-facets-drawer') &&
          window.matchMedia('(max-width: 999px)').matches
        ) {
          return 'bottom';
        }
        return original.get.call(this);
      }
    });
    FacetDrawer.prototype._collectionPadOpenFrom = true;
  }

  if (window.customElements.get('facet-drawer')) {
    patchCollectionDrawerOpenFrom();
  } else if (window.customElements.whenDefined) {
    window.customElements.whenDefined('facet-drawer').then(patchCollectionDrawerOpenFrom);
  }

  var SERIES_PARAM = 'filter.p.m.custom.product_series';

  /**
   * @param {Event} event
   * @param {string} selector
   * @returns {Element | null}
   */
  function findFromEvent(event, selector) {
    var path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (node && node.nodeType === 1 && node.matches && node.matches(selector)) {
        return node;
      }
    }
    var target = event.target;
    if (target && target.nodeType !== 1) {
      target = target.parentElement;
    }
    return target && target.closest ? target.closest(selector) : null;
  }

  /**
   * @param {Element} fromEl
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  function findDrawer(fromEl, event) {
    if (fromEl && fromEl.closest) {
      var nested = fromEl.closest('facet-drawer');
      if (nested) return nested;
    }
    var path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    for (var j = 0; j < path.length; j++) {
      var node = path[j];
      if (node && node.tagName === 'FACET-DRAWER') return node;
    }
    return document.querySelector('facet-drawer.collection-facets-drawer');
  }

  /**
   * Clear sheet filters except product_series (driven by collection-img-tab).
   * @param {HTMLElement} drawer
   */
  function resetDrawerForm(drawer) {
    var form =
      drawer.querySelector('form.facets-vertical') ||
      drawer.querySelector('#facet-form') ||
      drawer.querySelector('form');
    if (!form) return;

    form.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
      if (input.name === SERIES_PARAM || input.closest('.facet-series-fields')) {
        return;
      }
      input.checked = false;
      input.indeterminate = false;
      input.removeAttribute('checked');
    });

    form.querySelectorAll('union-select').forEach((unionSelect) => {
      if (typeof unionSelect._updateUnionSelectState === 'function') {
        unionSelect._updateUnionSelectState();
      }
    });

    form.querySelectorAll('price-range').forEach((rangeRoot) => {
      var minText = rangeRoot.querySelector('input[name="filter.v.price.gte"]');
      var maxText = rangeRoot.querySelector('input[name="filter.v.price.lte"]');
      var minRange = rangeRoot.querySelector('input[type="range"]:first-child');
      var maxRange = rangeRoot.querySelector('input[type="range"]:last-child');
      if (minText) minText.value = '';
      if (maxText) maxText.value = '';
      if (minRange) {
        minRange.value = minRange.min || '0';
        if (minRange.parentElement) {
          minRange.parentElement.style.setProperty('--range-min', '0%');
        }
      }
      if (maxRange) {
        maxRange.value = maxRange.max;
        if (maxRange.parentElement) {
          maxRange.parentElement.style.setProperty('--range-max', '100%');
        }
      }
      syncBounds(rangeRoot);
    });

    form._isDirty = true;
  }

  /**
   * Drop applied facet params except product_series; keep sort_by.
   * @returns {URL}
   */
  function buildCollectionResetUrl() {
    var url = new URL(window.location.href);
    Array.from(url.searchParams.keys()).forEach(function (key) {
      if (key === 'page' || (key.indexOf('filter.') === 0 && key !== SERIES_PARAM)) {
        url.searchParams.delete(key);
      }
    });
    return url;
  }

  /**
   * Bar Reset lives in collection-img-tab. facet-link would morph that section
   * instead of main-collection, so the product grid and drawer stay filtered.
   */
  function dispatchMainCollectionReset() {
    var mainSection = document.querySelector('.shopify-section--main-collection');
    var url = buildCollectionResetUrl();
    if (!mainSection || !mainSection.id) {
      window.location.href = url.toString();
      return;
    }
    var requestUrl = new URL(url.toString());
    requestUrl.searchParams.set('section_id', mainSection.id.replace(/^shopify-section-/, ''));
    document.dispatchEvent(
      new CustomEvent('facet:update', {
        bubbles: true,
        detail: { url: requestUrl }
      })
    );
  }

  /**
   * Close and Reset live in the body-appended drawer. composedPath is required
   * because Reset label clicks can land on a text node (closest is missing),
   * and slotted footer events may not parent-walk to the host the same way.
   */
  document.addEventListener(
    'click',
    function onCollectionDrawerClick(event) {
      var closeBtn = findFromEvent(event, '.collection-facets-drawer__close');
      if (closeBtn) {
        event.preventDefault();
        event.stopPropagation();
        var closeDrawer = findDrawer(closeBtn, event);
        if (closeDrawer && typeof closeDrawer.hide === 'function') {
          closeDrawer.hide();
        }
        return;
      }

      var drawerResetBtn = findFromEvent(event, '.collection-facets-drawer__reset');
      if (drawerResetBtn) {
        event.preventDefault();
        event.stopImmediatePropagation();
        var drawer = findDrawer(drawerResetBtn, event);
        if (drawer) resetDrawerForm(drawer);
        return;
      }

      var barResetBtn = findFromEvent(event, '.collection-filter-bar__reset');
      if (!barResetBtn) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      var openDrawer = document.querySelector('facet-drawer.collection-facets-drawer');
      if (openDrawer) resetDrawerForm(openDrawer);
      dispatchMainCollectionReset();
    },
    true
  );
})();
